"use client";

import { useCallback, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useToast } from "@/hooks/useToast";
import CreatePasswordContent from "../create-password/CreatePasswordContent";
import { useResetPassword } from "@/hooks/auth/useAuth";
import { zodResolver } from "@hookform/resolvers/zod";
import { PasswordFormSchema, ResetPasswordParamSchema } from "@/types/auth";

type ResetPasswordFormFields = {
  password: string;
  confirmPassword: string;
};

const getResetPasswordPayload = (
  token: string,
  data: ResetPasswordFormFields,
) => {
  return ResetPasswordParamSchema.parse({
    token,
    password: data.password,
    confirmPassword: data.confirmPassword,
  });
};

const ResetPasswordStep = () => {
  const {
    phone,
    email,
    authMethod,
    verificationToken,
    openPhoneLogin,
    openEmailLogin,
  } = useAuthModal();
  const { toast } = useToast();

  const methods = useForm<ResetPasswordFormFields>({
    resolver: zodResolver(PasswordFormSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const { handleSubmit, watch, setError, clearErrors } = methods;

  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");

  // Guard: if verification token is missing, redirect back to login
  useEffect(() => {
    if (verificationToken) return;

    if (authMethod === "email") {
      void openEmailLogin(email);
      return;
    }

    void openPhoneLogin(phone);
  }, [
    authMethod,
    email,
    openEmailLogin,
    openPhoneLogin,
    phone,
    verificationToken,
  ]);

  const { mutate: resetPassword, isPending: isResetting } = useResetPassword({
    onSuccess: (response) => {
      toast({
        variant: "success",
        title: "Success",
        description: response.message,
      });

      if (authMethod === "email") {
        void openEmailLogin(email);
      } else {
        void openPhoneLogin(phone);
      }
    },
    onError: (message) => {
      setError("password", {
        type: "server",
        message,
      });
    },
  });

  const onSubmit = useCallback(
    (data: ResetPasswordFormFields) => {
      if (!verificationToken || isResetting) return;

      clearErrors();

      try {
        const payload = getResetPasswordPayload(verificationToken, data);
        resetPassword(payload);
      } catch {
        setError("root", {
          type: "validate",
          message: "Invalid password requirements",
        });
      }
    },
    [clearErrors, isResetting, resetPassword, setError, verificationToken],
  );

  return (
    <FormProvider {...methods}>
      <CreatePasswordContent
        passwordValue={passwordValue}
        confirmPasswordValue={confirmPasswordValue}
        onSubmit={handleSubmit(onSubmit)}
        isSubmitting={isResetting}
        submitLabel="Submit"
      />
    </FormProvider>
  );
};

export default ResetPasswordStep;
